// v49.2 / v49.18 — Shared nav config.
// Single source of truth for the platform's destinations. Sidebar
// filters by role; topbar resolves the page title from the current
// pathname.
//
// v49.18 — restructured to match the v48 SIDEBAR_DESTINATIONS order
// (coordinator + partner variants). Adds /projects and /futures.
// Per-role label override via `labelByRole` so /partners shows as
// "Find Work" on the partner side but "Supplier Pool" for coord/
// hybrid. The actual brand row + version sit in Sidebar.tsx so they
// aren't represented here.

import type { Role } from "@/lib/api/types";

export interface NavSubItem {
  id:     string;
  label:  string;
}

export interface NavItem {
  to:           string;
  label:        string;
  roles:        Role[];
  /* Optional per-role label override. When the active role matches a
     key here, that label wins over `label`. */
  labelByRole?: Partial<Record<Role, string>>;
  icon?:        string;
  /* Optional sub-items rendered as an expandable inline list beneath
     the destination when it's active. Matches v48 sidebar pattern. */
  subItems?:    NavSubItem[];
  /* Per-role sub-item override (e.g. partner sees a different set). */
  subItemsByRole?: Partial<Record<Role, NavSubItem[]>>;
}

export const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard",     label: "Home",              roles: ["coordinator", "partner", "hybrid"] },
  { to: "/notifications", label: "Notifications",     roles: ["coordinator", "partner", "hybrid"] },
  { to: "/inbox",         label: "Mail",              roles: ["coordinator", "partner", "hybrid"] },
  { to: "/projects",      label: "My Projects",       roles: ["coordinator", "partner", "hybrid"] },
  /* Coord-only marketplace ordering: Live Requirements → Active Deals → Supplier Pool.
     v50.197 — Partners see this same destination labelled "Find Work"
     since /requirements is where their labour-call feed actually lives.
     The previous wiring routed partners' "Find Work" to /partners
     (SupplierPoolPage), which showed other suppliers — nonsensical for
     the supply side. */
  {
    to:          "/requirements",
    label:       "Live Requirements",
    labelByRole: { partner: "Find Work" },
    roles:       ["coordinator", "partner", "hybrid"],
  },
  { to: "/deals",         label: "Active Deals",      roles: ["coordinator", "partner", "hybrid"] },
  /* /partners is "Supplier Pool" — buyer-side only. Partners don't pick
     other partners; the entry is hidden for the partner role per
     v50.197. Hybrid keeps it since their coord side needs it. */
  {
    to:          "/partners",
    label:       "Supplier Pool",
    roles:       ["coordinator", "hybrid"],
    subItems:    [
      { id: "find",         label: "Find Suppliers" },
      { id: "partners",     label: "Partners" },
      { id: "tried-tested", label: "Tried & Tested" },
      { id: "map",          label: "Find local suppliers" },
      { id: "network",      label: "Network" },
      { id: "urgent",       label: "Urgent" },
    ],
  },
  { to: "/workers",       label: "My Crew",           roles: ["partner",     "hybrid"] },
  /* v49.85 — Move 3 Bid Engine. Visible to all roles in M2 onwards
     (v49.91): coordinators post / review opportunities, partners bid. */
  { to: "/tenders",       label: "Open Tenders",      roles: ["coordinator", "partner", "hybrid"] },
  { to: "/futures",       label: "Labour Futures",    roles: ["coordinator", "partner", "hybrid"] },
  { to: "/compliance",    label: "Compliance Wallet", roles: ["partner",     "hybrid"] },
  { to: "/earnings",      label: "Earnings",          roles: ["partner",     "hybrid"] },
  { to: "/payments",      label: "Payments",          roles: ["coordinator", "partner", "hybrid"] },
  /* v50.328 — Defence P2: e-signed coord↔partner framework agreements. */
  { to: "/frameworks",    label: "My Frameworks",     roles: ["coordinator", "partner", "hybrid"] },
  { to: "/disputes",      label: "Disputes",          roles: ["coordinator", "partner", "hybrid"] },
  { to: "/insights",      label: "Insights",          roles: ["coordinator", "hybrid"] },
  { to: "/audit",         label: "Audit log",         roles: ["coordinator", "partner", "hybrid"] },
  { to: "/settings",      label: "Settings",          roles: ["coordinator", "partner", "hybrid"] },
];

export function visibleNavForRole(role: Role | undefined | null): NavItem[] {
  if (!role) return [];
  return NAV_ITEMS
    .filter(n => n.roles.includes(role))
    .map(n => ({
      ...n,
      label:    (n.labelByRole && n.labelByRole[role]) || n.label,
      subItems: (n.subItemsByRole && n.subItemsByRole[role]) || n.subItems,
    }));
}

/* Resolve the label for a pathname so the topbar can show the current
   page title without each route component owning its own header copy. */
export function pageTitleFor(pathname: string, role?: Role | null): string {
  const items = role ? visibleNavForRole(role) : NAV_ITEMS;
  /* Exact match first, then longest-prefix match, then fallback. */
  const exact = items.find(n => n.to === pathname);
  if (exact) return exact.label;
  const prefix = items
    .filter(n => n.to !== "/" && pathname.startsWith(n.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (prefix) return prefix.label;
  return "LabourCore";
}
